import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from './storage.service';
import { StorageKeys } from './storage-keys';

export interface CustomTheme {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  slug: string;
  mode: LightDarkMode;
}

type LightDarkMode = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private customThemes: CustomTheme[] = [
    { id: 'violet-dream-1', name: 'Violet Dream', mode: 'dark', slug: 'violet-dream', primary: '#312057', secondary: '#8B5CF6' },
    { id: 'violet-dream', name: 'Violet Dream', mode: 'light', slug: 'violet-dream', primary: '#312057', secondary: '#8B5CF6' },
    { id: 'forest-mist', name: 'Forest Mist', mode: 'light', slug: 'forest-mist', primary: '#104533', secondary: '#10B981' },
    { id: 'black-gray', name: 'Black Gray', mode: 'dark', slug: 'black-gray', primary: '#fafafa', secondary: '#000000' },
    { id: 'white-black', name: 'White Black', mode: 'light', slug: 'white-black', primary: '#000000', secondary: '#EEEEEE' },
    { id: 'crimson-tide', name: 'Crimson Tide', mode: 'light', slug: 'crimson-tide', primary: '#601b1b', secondary: '#EF4444' },
    { id: 'desert-dusk', name: 'Desert Dusk', mode: 'dark', slug: 'desert-dusk', primary: '#ffe361', secondary: '#E11D48' },
    { id: 'tropical-vibes', name: 'Tropical Vibes', mode: 'dark', slug: 'tropical-vibes', primary: '#13c7b6', secondary: '#007164' },
    // { id: 'forest-mist', name: 'Forest Mist', mode:'dark', slug: 'forest-mist', primary: '#10B981', secondary: '#D4D4D4' },
    // { id: 'crimson-tide', name: 'Crimson Tide', mode:'dark', slug: 'crimson-tide', primary: '#EF4444', secondary: '#FFD700' },
    // { id: 'desert-dusk', name: 'Desert Dusk', mode:'dark', slug: 'desert-dusk', primary: '#E11D48', secondary: '#F5E8B7' },
    // { id: 'tropical-vibes', name: 'Tropical Vibes', mode:'dark', slug: 'tropical-vibes', primary: '#14B8A6', secondary: '#F59E0B' },

    // { id: 'forest-mist', name: 'Forest Mist', mode:'light', slug: 'forest-mist', primary: '#10B981', secondary: '#D4D4D4' },
    // { id: 'crimson-tide', name: 'Crimson Tide', mode:'light', slug: 'crimson-tide', primary: '#EF4444', secondary: '#FFD700' },
    // { id: 'desert-dusk', name: 'Desert Dusk', mode:'light', slug: 'desert-dusk', primary: '#E11D48', secondary: '#F5E8B7' },
    // { id: 'tropical-vibes', name: 'Tropical Vibes', mode:'light', slug: 'tropical-vibes', primary: '#14B8A6', secondary: '#F59E0B' },
  ];

  private lightDarkMode!: BehaviorSubject<LightDarkMode>;
  lightDarkMode$!: Observable<LightDarkMode>;
  private currentCustomTheme!: CustomTheme;

  constructor(private storageService: StorageService) {
    this.initializeTheme();
  }

  private async initializeTheme() {
    const savedCustomThemeId = await this.storageService.getItem<string>(StorageKeys.CUSTOM_THEME);
    this.currentCustomTheme = this.customThemes.find(theme => theme.id === savedCustomThemeId) || this.customThemes[0];

    const savedMode = await this.storageService.getItem<string>(StorageKeys.LIGHT_DARK_MODE);
    const defaultMode: LightDarkMode = 'light';
    const initialMode: LightDarkMode = savedMode === 'dark' || savedMode === 'light' ? savedMode : defaultMode;

    this.lightDarkMode = new BehaviorSubject<LightDarkMode>(initialMode);
    this.lightDarkMode$ = this.lightDarkMode.asObservable();

    this.applyCustomTheme(this.currentCustomTheme);
    this.applyLightDarkMode(initialMode);
  }

  get themes(): CustomTheme[] {
    return this.customThemes;
  }

  get currentTheme(): CustomTheme {
    return this.currentCustomTheme;
  }

  get isDarkMode(): boolean {
    return this.lightDarkMode.value === 'dark';
  }

  async setCustomTheme(themeId: string, mode?: LightDarkMode) {
    const theme = this.customThemes.find(t => t.id === themeId) || this.customThemes[0];
    this.currentCustomTheme = theme;
    this.applyCustomTheme(theme);
    await this.storageService.setItem(StorageKeys.CUSTOM_THEME, theme.id);
    if (mode) {
      this.lightDarkMode.next(mode);
      this.applyLightDarkMode(mode);
      await this.storageService.setItem(StorageKeys.LIGHT_DARK_MODE, mode);
    }
  }

  async toggleLightDarkMode() {
    const newMode: LightDarkMode = this.lightDarkMode.value === 'dark' ? 'light' : 'dark';
    this.lightDarkMode.next(newMode);
    this.applyLightDarkMode(newMode);
    await this.storageService.setItem(StorageKeys.LIGHT_DARK_MODE, newMode);
  }

  private applyCustomTheme(theme: CustomTheme) {
    const root = document.documentElement;
    root.style.setProperty('--ion-color-primary', theme.primary);
    root.style.setProperty('--ion-color-primary-rgb', hexToRgb(theme.primary));
    root.style.setProperty('--ion-color-primary-contrast', getContrastColor(theme.primary));
    root.style.setProperty('--ion-color-secondary', theme.secondary);
    root.style.setProperty('--ion-color-secondary-rgb', hexToRgb(theme.secondary));
    root.style.setProperty('--ion-color-secondary-contrast', getContrastColor(theme.secondary));
  }

  private applyLightDarkMode(mode: LightDarkMode) {
    if (mode === 'dark') {
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.body.removeAttribute('data-theme');
    }
  }
}

// Helper functions
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}

function getContrastColor(hex: string): string {
  const rgb = hexToRgb(hex).split(',').map(Number);
  const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
}